import { looseIndex } from '#library/object.library.js';
import { sym as lib, $Target, $Discover, $Extensible, $Inspect, $LogConfig, $Registry, $Register as $LibRegister, $SerializerRegistry, $Identity, $ImmutableSkip } from '#library/symbol.library.js';
export { $Target, $Discover, $Extensible, $Inspect, $LogConfig, $Registry, $LibRegister, $SerializerRegistry, $Identity, $ImmutableSkip };
/** check valid Tempo instance */
export const isTempo = (tempo) => tempo?.[sym.$Identity] === true;
/**
 * Centralized registry for all Tempo-specific Global Symbols.
 * These symbols utilize Symbol.for() to ensure consistency across module boundaries.
 */
export const TermError = Symbol.for('magmacomputing/tempo/termError');
/** @internal unique symbols for critical internal accessors */
/** key for Global Discovery of Tempo configuration */ export const $Tempo = Symbol.for('$Tempo');
/** key for Reactive Plugin Registration */ export const $Register = Symbol.for('magmacomputing/tempo/register');
/** key for Internal Interpreter Service */ export const $Interpreter = Symbol.for('magmacomputing/tempo/interpreter');
/** key for Master Guard */ export const $guard = Symbol.for('magmacomputing/tempo/guard');
/** internal key for signaling pre-errored state */ export const $errored = Symbol.for('magmacomputing/tempo/errored');
/** internal key for accessing private instance state */ export const $Internal = Symbol.for('magmacomputing/tempo/internal');
/** hardened globalThis bridge key for the TempoRuntime */ export const $Bridge = Symbol.for('magmacomputing/tempo/runtime');
/** cross-bundle brand check for TempoRuntime */ export const $RuntimeBrand = Symbol.for('magmacomputing/tempo/runtime/brand');
/** branding for explicit PropertyDescriptors */ export const $Descriptor = Symbol.for('magmacomputing/tempo/descriptor');
/** key for License State */ export const $License = Symbol.for('magmacomputing/tempo/license');
/** key for Plugin Discrimination */ export const $PluginType = Symbol.for('magmacomputing/tempo/pluginType');
/** @internal static config helper */ export const $setConfig = Symbol.for('magmacomputing/tempo/setConfig');
/** @internal static discovery helper */ export const $setDiscovery = Symbol.for('magmacomputing/tempo/setDiscovery');
/** @internal static event builder */ export const $setEvents = Symbol.for('magmacomputing/tempo/setEvents');
/** @internal static period builder */ export const $setPeriods = Symbol.for('magmacomputing/tempo/setPeriods');
/** @internal static alias builder */ export const $setAliases = Symbol.for('magmacomputing/tempo/setAliases');
/** @internal static guard builder */ export const $buildGuard = Symbol.for('magmacomputing/tempo/buildGuard');
/** @internal static base class marker */ export const $IsBase = Symbol.for('magmacomputing/tempo/isBase');
/** @internal static license scope status mutator */ export const $updateScopeStatus = Symbol.for('magmacomputing/tempo/updateScopeStatus');
/** @internal Tempo Symbol Registry (Local Keys) */
const local = {
    $Tempo, $Register, $Interpreter, $guard, $errored,
    $Internal, $Bridge, $RuntimeBrand, $Descriptor, $License, $setConfig, $setDiscovery,
    $setEvents, $setPeriods, $setAliases, $buildGuard, $IsBase, $PluginType, $updateScopeStatus
};
/** @internal Unified Symbol Registry (Inherits from #library via Prototype Chain) */
export const sym = Object.assign(Object.create(lib), local);
/** @internal Tempo Token registry */
export const Token = looseIndex()({
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ Snippet Symbols
    /** year */ yy: Symbol('yy'),
    /** ISO yearOfWeek */ yw: Symbol('yw'),
    /** month */ mm: Symbol('mm'),
    /** week */ ww: Symbol('ww'),
    /** day */ dd: Symbol('dd'),
    /** hour */ hh: Symbol('hh'),
    /** minute */ mi: Symbol('mi'),
    /** second */ ss: Symbol('ss'),
    /** fraction */ ff: Symbol('ff'),
    /** ordinal suffix */ ord: Symbol('ord'),
    /** meridiem */ mer: Symbol('mer'),
    /** short weekday name */ www: Symbol('www'),
    /** relative-suffix */ afx: Symbol('afx'),
    /** time-suffix */ sfx: Symbol('sfx'),
    /** time unit */ unt: Symbol('unt'),
    /** separator */ sep: Symbol('sep'),
    /** modifier */ mod: Symbol('mod'),
    /** generic number */ nbr: Symbol('nbr'),
    /** Tempo slick shorthand */ slk: Symbol('slk'),
    /** Tempo event */ evt: Symbol('evt'),
    /** Tempo period */ per: Symbol('per'),
    /** time zone offset */ tzd: Symbol('tzd'),
    /** historical era */ era: Symbol('era'),
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ Layout Symbols
    /** date */ dt: Symbol('date'),
    /** time */ tm: Symbol('time'),
    /** compact time (hhmiss) */ hms: Symbol('hourMinuteSecond'),
    /** compact day-month-year (ddmmyy) */ dmy6: Symbol('dayMonthYearShort'),
    /** compact month-day-year (mmddyy) */ mdy6: Symbol('monthDayYearShort'),
    /** compact year-month-day (yymmdd) */ ymd6: Symbol('yearMonthDayShort'),
    /** date and time */ dtm: Symbol('dateTime'),
    /** time and date */ tmd: Symbol('timeDate'),
    /** day-month-year */ dmy: Symbol('dayMonthYear'),
    /** month-day-year */ mdy: Symbol('monthDayYear'),
    /** year-month-day */ ymd: Symbol('yearMonthDay'),
    /** day of month offset */ off: Symbol('offset'),
    /** weekDay */ wkd: Symbol('weekDay'),
    /** relative offset (years, days, hours, etc) */ rel: Symbol('relativeOffset'),
    /** timezone/calendar brackets */ brk: Symbol('brackets'),
    /** year and era */ ye: Symbol('yearEra'),
    /** era and year (leading) */ ey: Symbol('eraYearLeading'),
});
